import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { cn } from '@/new/lib/utils';

export interface CopyMappingsSource {
  id: string | number;
  /** Primary label for the source (file name / workspace name). */
  name: string;
  /** Muted secondary label shown next to the name (date / workspace id). */
  meta?: ReactNode;
  /** Render the meta label in a monospace font (e.g. for ids). */
  metaMono?: boolean;
  fieldCount: number;
}

interface CopyMappingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  description: ReactNode;
  sources: CopyMappingsSource[];
  searchPlaceholder: (count: number) => string;
  emptyText: string;
  noMatchText: string;
  /** Return `false` to keep the dialog open (e.g. the copy failed to apply). */
  onConfirm: (id: CopyMappingsSource['id']) => void | boolean;
  /** Called whenever the selected source changes (e.g. to prefetch its mapping). */
  onSelectionChange?: (id: CopyMappingsSource['id'] | null) => void;
  confirmDisabled?: boolean;
  isConfirmLoading?: boolean;
}

export function CopyMappingsDialog({
  open,
  onOpenChange,
  description,
  sources,
  searchPlaceholder,
  emptyText,
  noMatchText,
  onConfirm,
  onSelectionChange,
  confirmDisabled = false,
  isConfirmLoading = false,
}: CopyMappingsDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState<CopyMappingsSource['id'] | null>(null);

  // Reset state whenever the dialog is opened so a stale pick/search doesn't linger.
  useEffect(() => {
    if (open) {
      setSelectedSourceId(null);
      setSearchQuery('');
    }
  }, [open]);

  useEffect(() => {
    onSelectionChange?.(selectedSourceId);
  }, [selectedSourceId, onSelectionChange]);

  const filteredSources = useMemo(
    () => sources.filter((s) => s.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [sources, searchQuery],
  );

  const handleConfirm = () => {
    if (selectedSourceId === null) return;
    if (onConfirm(selectedSourceId) === false) return;
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Icon className="lucide--clipboard-paste" />
              <span>Copy field mappings</span>
            </div>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="-mx-3 -mt-2">
          <SearchInput
            placeholder={searchPlaceholder(filteredSources.length)}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex h-[45vh] flex-col overflow-y-auto px-1 pt-2">
            {filteredSources.length === 0 && (
              <p className="my-12 text-center text-gray-400">{searchQuery ? noMatchText : emptyText}</p>
            )}

            {filteredSources.map((source) => {
              const isSelected = selectedSourceId === source.id;
              return (
                <div key={source.id} className="group">
                  <button
                    type="button"
                    onClick={() => setSelectedSourceId(source.id)}
                    className={cn(
                      'my-1 flex w-full cursor-pointer items-center justify-between gap-3 rounded-md p-2.5 px-3 text-left duration-150',
                      isSelected ? 'bg-teal-50/50 ring-1 ring-teal-400' : 'hover:bg-gray-50',
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className={cn('truncate font-medium', isSelected ? 'text-teal-900' : 'text-gray-700')}>
                        {source.name}
                      </span>
                      {source.meta !== undefined && (
                        <span className={cn('flex-shrink-0 text-gray-400', source.metaMono && 'font-mono')}>
                          {source.meta}
                        </span>
                      )}
                    </div>
                    <span className="flex-shrink-0 text-gray-500">
                      {source.fieldCount} field{source.fieldCount === 1 ? '' : 's'}
                    </span>
                  </button>
                  <hr className="mx-1 my-px group-last:opacity-0" />
                </div>
              );
            })}
          </div>
        </div>

        <div className="-mx-7 mt-6 -mb-6.5 flex justify-end gap-2 border-t border-gray-200 bg-white px-7 pt-4 pb-5">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={selectedSourceId === null || confirmDisabled || isConfirmLoading}
          >
            {isConfirmLoading ? (
              <>
                <Icon className="lucide--loader animate-spin ease-in-out" />
                Loading...
              </>
            ) : (
              <>
                <Icon className="lucide--clipboard-paste" />
                Copy mappings
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
